import {
  useCallback,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react'
import { AutocompleteProps, AutocompleteRef, ItemType } from './types'
import { Input } from '@frontend/design_system'
import { AutocompleteDropdown } from './components/AutocompleteDropdown'
import styles from './styles.module.scss'
import { joinClasses } from 'utils/joinClasses'
import { useOutsideClick } from 'ui/hooks/useOutsideClick'

export const Autocomplete = forwardRef<AutocompleteRef, AutocompleteProps>(
  (
    {
      placeholder,
      size,
      items,
      selectedValue,
      onChange,
      label,
      isRequired,
      error,
      noSpaceForError,
      className = '',
      tooltip,
      search,
      disabled,
      notFoundTitle,
      maxLength,
    },
    ref
  ) => {
    const [selectEl, setSelectEl] = useState<HTMLDivElement | null>(null)
    const [inputValue, setInputValue] = useState('')
    const [showDropdown, setShowDropdown] = useState(false)

    const closeDropdown = useCallback(() => setShowDropdown(false), [])
    const openDropdown = useCallback(
      () => !disabled && setShowDropdown(true),
      [disabled]
    )
    const toggleDropdown = useCallback(
      () => !disabled && setShowDropdown((prev) => !prev),
      [disabled]
    )

    useImperativeHandle(ref, () => ({
      open: openDropdown,
      close: closeDropdown,
      toggle: toggleDropdown,
    }))

    const onRightIconClick = () => {
      if (showDropdown) {
        closeDropdown()
        setInputValue('')
        onChange('', { label: '', value: '' })
      } else {
        openDropdown()
      }
    }

    useEffect(() => {
      setInputValue(selectedValue)
    }, [selectedValue])

    const handleInputChange = (value: string) => {
      setInputValue(value)
      const currentItem = items.find((item) => item.label === value)
      if (currentItem) {
        onChange(currentItem.label, currentItem)
      } else {
        onChange(value, { label: value, value: '' })
      }
    }

    const handleChange = (value: string, fullData: ItemType) => {
      closeDropdown()
      onChange(value, fullData)
    }

    useOutsideClick(selectEl, closeDropdown)

    const renderDropdown = () => {
      if (!showDropdown) {
        return null
      }

      return (
        <AutocompleteDropdown
          items={items}
          selectedValue={selectedValue}
          onChange={handleChange}
          noSpaceForError={noSpaceForError}
          search={search}
          notFoundTitle={notFoundTitle}
        />
      )
    }

    const getIconRight = () => {
      if (disabled) {
        return ''
      }
      return showDropdown ? 'CloseIcon' : 'ArrowBottomIcon'
    }

    return (
      <div
        ref={setSelectEl}
        className={joinClasses(styles.autocomplete, className)}
      >
        <Input
          iconRight={getIconRight()}
          iconColor="blackMaster"
          iconSize="small"
          typographyName="Button2"
          onClick={toggleDropdown}
          iconOnClickRight={onRightIconClick}
          placeholder={placeholder}
          tooltip={tooltip}
          size={size}
          onChange={handleInputChange}
          value={inputValue}
          label={label}
          isRequired={isRequired}
          error={error}
          maxLength={maxLength}
          isDisabled={disabled}
          readonly={disabled}
          spaceForError={noSpaceForError ? 'none' : 'default'}
        />
        {renderDropdown()}
      </div>
    )
  }
)
